import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { useQueryClient } from "react-query";
import { axiosInstance } from "./axios";
import {
  IBudgetChapter,
  IBudgetDivision,
  IBudgetParagraph,
} from "dto/Budget/IBudgetDictionaries";
import { IBudgetExpense } from "dto/Budget/IBudgetExpense";
import {
  IBudgetEngagement,
  IBudgetEngagementListItem,
} from "dto/Budget/IBudgetEngagement";
import { IBudgetInvoice } from "dto/Budget/IBudgetInvoice";
import { IBudgetContract } from "dto/Budget/IBudgetContract";
import { IBudgetTender } from "dto/Budget/IBudgetTender";
import { IBudgetInvestmentCase } from "dto/Budget/IBudgetInvestmentCase";
import { IBudgetExpensesReportItem } from "dto/Budget/IBudgetExpensesReport";
import {
  IEngagementSettlement,
  IInvoiceSettlement,
  ISettleableEngagement,
} from "dto/Budget/IBudgetSettlement";
import { IBudgetEngagementDocument } from "dto/Budget/IBudgetEngagementDocument";

/**
 * Realny klient REST modułu budżetowania (BE: /api/budget/*).
 * API serializuje PascalCase (zgodnie z DTO front-endu), więc pola mapują się 1:1.
 * Drobne różnice (Status zaangażowania, brak listy umów po stronie BE)
 * dopasowujemy w mapperach poniżej, bez zmian w komponentach.
 */

/** Domyślny rok budżetowy (gdy widok nie poda innego). */
export const DEFAULT_BUDGET_YEAR = new Date().getFullYear();

// ---------------------------------------------------------------------------
// Mappery odpowiedzi BE -> DTO front-endu
// ---------------------------------------------------------------------------

type BeEngagement = IBudgetEngagement & { AcceptationState?: string };

const engagementStatus = (e: BeEngagement): string => {
  if (e.Accepted) return "Zaakceptowany";
  if (e.AcceptationState === "rejected") return "Odrzucony";
  if (e.AcceptationState === "returned") return "Do poprawy";
  return "Do akceptacji";
};

const mapEngagement = <T extends BeEngagement>(e: T): T =>
  ({ ...e, Status: engagementStatus(e), Quotas: e.Quotas ?? [] } as T);

const mapInvoice = (i: IBudgetInvoice): IBudgetInvoice => ({
  ...i,
  Quotas: i.Quotas ?? [],
});

// ---------------------------------------------------------------------------
// Typy żądań
// ---------------------------------------------------------------------------

export interface ISaveExpenseQuotaRequest {
  ParagraphId: string;
  ParagraphSuffix: string;
  Amount: number;
}

export interface ICreateExpenseRequest {
  Year: number;
  DivisionId: string;
  ChapterId: string;
  Title: string;
  OpeningBalance: boolean;
  OpeningBalanceAmount: number;
  SharedTask: boolean;
  Quotas: ISaveExpenseQuotaRequest[];
}

export interface IUpdateExpenseQuotaRequest {
  /** Id istniejącej kwoty; brak = nowy paragraf. */
  Id?: string;
  ParagraphId: string;
  ParagraphSuffix: string;
  Amount: number;
}

export interface IUpdateExpenseRequest {
  ExpenseId: string;
  Title: string;
  SharedTask: boolean;
  Quotas: IUpdateExpenseQuotaRequest[];
}

export interface ISaveEngagementQuotaRequest {
  ExpenseQuotaId: string;
  Amount: number;
}

export interface ICreateEngagementRequest {
  ExpenseId: string;
  Title: string;
  EngagementDate: string;
  ExecutionDeadline: string;
  GrantingProcedure: string;
  GrantingProcedureDescription?: string;
  Justification?: string;
  Quotas: ISaveEngagementQuotaRequest[];
  /** Identyfikator zaangażowania korygowanego (dla korekt). */
  CorrectedEngagementId?: string | null;
}

// ---------------------------------------------------------------------------
// Funkcje API
// ---------------------------------------------------------------------------

const getBudgetYearsApi = async () =>
  (await axiosInstance.get<number[]>("/budget/years")).data;

const getDivisionsApi = async (search?: string) =>
  (
    await axiosInstance.get<IBudgetDivision[]>("/budget/divisions", {
      params: { search: search || undefined },
    })
  ).data;

const getChaptersApi = async (divisionId?: string, search?: string) =>
  (
    await axiosInstance.get<IBudgetChapter[]>("/budget/chapters", {
      params: { divisionId, search: search || undefined },
    })
  ).data;

const getParagraphsApi = async (search?: string) =>
  (
    await axiosInstance.get<IBudgetParagraph[]>("/budget/paragraphs", {
      params: { search: search || undefined },
    })
  ).data;

const getGrantingProceduresApi = async () =>
  (await axiosInstance.get<string[]>("/budget/granting-procedures")).data;

const getExpensesApi = async (year: number) =>
  (await axiosInstance.get<IBudgetExpense[]>("/budget/expenses", { params: { year } }))
    .data;

const getExpenseApi = async (id: string) =>
  (await axiosInstance.get<IBudgetExpense>(`/budget/expense/${id}`)).data;

const createExpenseApi = async (payload: ICreateExpenseRequest) =>
  (await axiosInstance.post<IBudgetExpense>("/budget/expense", payload)).data;

const updateExpenseApi = async (payload: IUpdateExpenseRequest) =>
  (await axiosInstance.put<IBudgetExpense>("/budget/expense", payload)).data;

const getEngagementsApi = async (expenseId: string) =>
  (
    await axiosInstance.get<BeEngagement[]>(
      `/budget/expense/${expenseId}/engagements`,
    )
  ).data.map(mapEngagement);

const getAllEngagementsApi = async (year: number) =>
  (
    await axiosInstance.get<(IBudgetEngagementListItem & { AcceptationState?: string })[]>(
      "/budget/engagements",
      { params: { year } },
    )
  ).data.map(mapEngagement);

const getEngagementInvoicesApi = async (engagementId: string) =>
  (
    await axiosInstance.get<IBudgetInvoice[]>(
      `/budget/engagement/${engagementId}/invoices`,
    )
  ).data.map(mapInvoice);

const getInvoicesApi = async (expenseId: string) =>
  (await axiosInstance.get<IBudgetInvoice[]>(`/budget/expense/${expenseId}/invoices`))
    .data.map(mapInvoice);

const getContractsApi = async (expenseId: string): Promise<IBudgetContract[]> =>
  (
    await axiosInstance.get<IBudgetContract[]>(
      `/budget/expense/${expenseId}/contracts`,
    )
  ).data;

const getTendersApi = async (expenseId: string): Promise<IBudgetTender[]> =>
  (
    await axiosInstance.get<IBudgetTender[]>(
      `/budget/expense/${expenseId}/tenders`,
    )
  ).data;

const getInvestmentCasesApi = async (
  expenseId: string,
): Promise<IBudgetInvestmentCase[]> =>
  (
    await axiosInstance.get<IBudgetInvestmentCase[]>(
      `/budget/expense/${expenseId}/investment-cases`,
    )
  ).data;

/**
 * `team` = zakres z „Widoku budżetu" w metryce pracownika (wydział / urząd) — BE sam
 * zawęża go do tego, na co pozwala metryka; bez niego raport dotyczy własnego budżetu.
 */
const getExpensesReportApi = async (year: number, team = false) =>
  (
    await axiosInstance.get<IBudgetExpensesReportItem[]>("/budget/report", {
      params: { year, team },
    })
  ).data;

const createEngagementApi = async (payload: ICreateEngagementRequest) =>
  mapEngagement(
    (await axiosInstance.post<BeEngagement>("/budget/engagement", payload)).data,
  );

const deleteEngagementApi = async (engagementId: string) =>
  (await axiosInstance.delete(`/budget/engagement/${engagementId}`)).data;

/** Wysłanie zaangażowania (szkic / do poprawy / odrzucone) do procedury akceptacji. */
const submitEngagementApi = async (engagementId: string) =>
  mapEngagement(
    (await axiosInstance.post<BeEngagement>(`/budget/engagement/${engagementId}/submit`)).data,
  );

/** Zaangażowanie dla osoby akceptującej — spoza własnego budżetu (autoryzacja po przydziale). */
const getEngagementForApprovalApi = async (engagementId: string) =>
  mapEngagement(
    (
      await axiosInstance.get<IBudgetEngagementListItem & { AcceptationState?: string }>(
        `/budget/engagement/${engagementId}/approval`,
      )
    ).data,
  );

// --- Dokument wniosku o zaangażowanie (XML WnioZaangBudz + podpisy XAdES) ---

/** Dokument wniosku; null = zaangażowanie nie było w obiegu (BE odpowiada 204). */
const getEngagementDocumentApi = async (
  engagementId: string,
): Promise<IBudgetEngagementDocument | null> => {
  const res = await axiosInstance.get<IBudgetEngagementDocument | "">(
    `/budget/engagement/${engagementId}/document`,
  );
  return res.status === 204 || !res.data ? null : res.data;
};

export interface IAttachEngagementSignatureRequest {
  engagementId: string;
  ElementId: string;
  /** Pełny XML dokumentu z dołączonym podpisem (z wtyczki). */
  Xml: string;
  Version?: number;
}

const attachEngagementDocumentSignatureApi = async ({
  engagementId,
  ...body
}: IAttachEngagementSignatureRequest): Promise<IBudgetEngagementDocument> =>
  (
    await axiosInstance.post<IBudgetEngagementDocument>(
      `/budget/engagement/${engagementId}/document/signature`,
      body,
    )
  ).data;

// --- Rozliczanie zaangażowań (settlements) ---

export interface ISaveSettlementQuota {
  EngagementQuotaId: string;
  Amount: number;
}

export interface ISaveSettlementRequest {
  LinkId?: string | null;
  EngagementId: string;
  KSeFInvoiceId?: string | null;
  DocumentId?: string | null;
  ProForma: boolean;
  Quotas: ISaveSettlementQuota[];
}

const getSettleableEngagementsApi = async (year?: number) =>
  (
    await axiosInstance.get<ISettleableEngagement[]>(
      "/budget/settlement/engagements",
      { params: { year } },
    )
  ).data;

const getEngagementSettlementApi = async (
  engagementId: string,
  invoiceId?: string,
) =>
  (
    await axiosInstance.get<IEngagementSettlement>(
      `/budget/settlement/engagement/${engagementId}`,
      { params: { invoiceId } },
    )
  ).data;

const getInvoiceSettlementsApi = async (invoiceId: string) =>
  (
    await axiosInstance.get<IInvoiceSettlement[]>(
      `/budget/settlement/invoice/${invoiceId}`,
    )
  ).data;

const saveSettlementApi = async (payload: ISaveSettlementRequest) =>
  (await axiosInstance.post<IEngagementSettlement>("/budget/settlement", payload))
    .data;

const deleteSettlementApi = async (linkId: string) =>
  (await axiosInstance.delete(`/budget/settlement/${linkId}`)).data;

// ---------------------------------------------------------------------------
// Klucze zapytań
// ---------------------------------------------------------------------------

const queryKeys = {
  years: ["budget.years"],
  divisions: (search?: string) => ["budget.divisions", search ?? ""],
  chapters: (divisionId?: string, search?: string) => [
    "budget.chapters",
    divisionId ?? null,
    search ?? "",
  ],
  paragraphs: (search?: string) => ["budget.paragraphs", search ?? ""],
  grantingProcedures: ["budget.grantingProcedures"],
  expenses: (year: number) => ["budget.expenses", year],
  expense: (id: string) => ["budget.expense", id],
  engagements: (expenseId: string) => ["budget.engagements", expenseId],
  allEngagements: (year: number) => ["budget.allEngagements", year],
  engagementInvoices: (engagementId: string) => [
    "budget.engagementInvoices",
    engagementId,
  ],
  invoices: (expenseId: string) => ["budget.invoices", expenseId],
  contracts: (expenseId: string) => ["budget.contracts", expenseId],
  tenders: (expenseId: string) => ["budget.tenders", expenseId],
  investmentCases: (expenseId: string) => ["budget.investmentCases", expenseId],
  expensesReport: (year: number, team = false) => [
    "budget.expensesReport",
    year,
    team,
  ],
  settleableEngagements: (year?: number) => [
    "budget.settleableEngagements",
    year ?? null,
  ],
  engagementSettlement: (engagementId: string, invoiceId?: string) => [
    "budget.engagementSettlement",
    engagementId,
    invoiceId ?? null,
  ],
  invoiceSettlements: (invoiceId: string) => [
    "budget.invoiceSettlements",
    invoiceId,
  ],
};

// ---------------------------------------------------------------------------
// Hooki
// ---------------------------------------------------------------------------

const useGetBudgetYears = () =>
  useCustomQuery<number[]>(queryKeys.years, getBudgetYearsApi);

const useGetDivisions = (search?: string) =>
  useCustomQuery<IBudgetDivision[]>(queryKeys.divisions(search), () =>
    getDivisionsApi(search),
  );

const useGetChapters = (divisionId?: string, search?: string) =>
  useCustomQuery<IBudgetChapter[]>(
    queryKeys.chapters(divisionId, search),
    () => getChaptersApi(divisionId, search),
    { enabled: !!divisionId },
  );

const useGetParagraphs = (search?: string) =>
  useCustomQuery<IBudgetParagraph[]>(queryKeys.paragraphs(search), () =>
    getParagraphsApi(search),
  );

const useGetGrantingProcedures = () =>
  useCustomQuery<string[]>(queryKeys.grantingProcedures, getGrantingProceduresApi);

const useGetExpenses = (year: number) =>
  useCustomQuery<IBudgetExpense[]>(queryKeys.expenses(year), () =>
    getExpensesApi(year),
  );

const useGetExpense = (id: string, enabled = true) =>
  useCustomQuery<IBudgetExpense | null>(
    queryKeys.expense(id),
    () => getExpenseApi(id),
    { enabled: enabled && !!id },
  );

const useGetEngagements = (expenseId: string, enabled = true) =>
  useCustomQuery<IBudgetEngagement[]>(
    queryKeys.engagements(expenseId),
    () => getEngagementsApi(expenseId),
    { enabled: enabled && !!expenseId },
  );

const useGetAllEngagements = (year: number) =>
  useCustomQuery<IBudgetEngagementListItem[]>(
    queryKeys.allEngagements(year),
    () => getAllEngagementsApi(year),
  );

const useGetEngagementInvoices = (engagementId: string, enabled = true) =>
  useCustomQuery<IBudgetInvoice[]>(
    queryKeys.engagementInvoices(engagementId),
    () => getEngagementInvoicesApi(engagementId),
    { enabled: enabled && !!engagementId },
  );

const useGetInvoices = (expenseId: string, enabled = true) =>
  useCustomQuery<IBudgetInvoice[]>(
    queryKeys.invoices(expenseId),
    () => getInvoicesApi(expenseId),
    { enabled: enabled && !!expenseId },
  );

const useGetContracts = (expenseId: string, enabled = true) =>
  useCustomQuery<IBudgetContract[]>(
    queryKeys.contracts(expenseId),
    () => getContractsApi(expenseId),
    { enabled: enabled && !!expenseId },
  );

const useGetTenders = (expenseId: string, enabled = true) =>
  useCustomQuery<IBudgetTender[]>(
    queryKeys.tenders(expenseId),
    () => getTendersApi(expenseId),
    { enabled: enabled && !!expenseId },
  );

const useGetInvestmentCases = (expenseId: string, enabled = true) =>
  useCustomQuery<IBudgetInvestmentCase[]>(
    queryKeys.investmentCases(expenseId),
    () => getInvestmentCasesApi(expenseId),
    { enabled: enabled && !!expenseId },
  );

const useGetExpensesReport = (year: number, team = false) =>
  useCustomQuery<IBudgetExpensesReportItem[]>(
    queryKeys.expensesReport(year, team),
    () => getExpensesReportApi(year, team),
  );

const useCreateExpense = () => {
  const queryClient = useQueryClient();
  return useCustomMutation<IBudgetExpense, ICreateExpenseRequest>(createExpenseApi, {
    onSuccess: (expense) => {
      queryClient.invalidateQueries(queryKeys.expenses(expense.Year));
    },
  });
};

const useGetSettleableEngagements = (year?: number) =>
  useCustomQuery<ISettleableEngagement[]>(
    queryKeys.settleableEngagements(year),
    () => getSettleableEngagementsApi(year),
  );

const useGetEngagementSettlement = (
  engagementId: string,
  invoiceId?: string,
  enabled = true,
) =>
  useCustomQuery<IEngagementSettlement>(
    queryKeys.engagementSettlement(engagementId, invoiceId),
    () => getEngagementSettlementApi(engagementId, invoiceId),
    { enabled: enabled && !!engagementId },
  );

const useGetInvoiceSettlements = (invoiceId: string, enabled = true) =>
  useCustomQuery<IInvoiceSettlement[]>(
    queryKeys.invoiceSettlements(invoiceId),
    () => getInvoiceSettlementsApi(invoiceId),
    { enabled: enabled && !!invoiceId },
  );

const invalidateSettlements = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries(["budget.invoiceSettlements"]);
  queryClient.invalidateQueries(["budget.engagementSettlement"]);
  queryClient.invalidateQueries(["budget.settleableEngagements"]);
  queryClient.invalidateQueries(["budget.allEngagements"]);
  queryClient.invalidateQueries(["budget.expenses"]);
  queryClient.invalidateQueries(["budget.engagementInvoices"]);
  queryClient.invalidateQueries(["budget.invoices"]);
};

const useSaveSettlement = () => {
  const queryClient = useQueryClient();
  return useCustomMutation<IEngagementSettlement, ISaveSettlementRequest>(
    saveSettlementApi,
    { onSuccess: () => invalidateSettlements(queryClient) },
  );
};

const useDeleteSettlement = () => {
  const queryClient = useQueryClient();
  return useCustomMutation<unknown, string>(deleteSettlementApi, {
    onSuccess: () => invalidateSettlements(queryClient),
  });
};

const useDeleteEngagement = () => {
  const queryClient = useQueryClient();
  return useCustomMutation<unknown, string>(deleteEngagementApi, {
    onSuccess: () => {
      queryClient.invalidateQueries(["budget.allEngagements"]);
      queryClient.invalidateQueries(["budget.engagements"]);
      queryClient.invalidateQueries(["budget.expenses"]);
    },
  });
};

const useUpdateExpense = () => {
  const queryClient = useQueryClient();
  return useCustomMutation<IBudgetExpense, IUpdateExpenseRequest>(updateExpenseApi, {
    onSuccess: (expense) => {
      queryClient.invalidateQueries(queryKeys.expenses(expense.Year));
      queryClient.invalidateQueries(queryKeys.expense(expense.Id));
    },
  });
};

const useSubmitEngagement = () => {
  const queryClient = useQueryClient();
  return useCustomMutation<IBudgetEngagement, string>(submitEngagementApi, {
    onSuccess: () => {
      queryClient.invalidateQueries(["budget.allEngagements"]);
      queryClient.invalidateQueries(["budget.engagements"]);
      queryClient.invalidateQueries(["workflowInstance"]);
    },
  });
};

const ENGAGEMENT_DOCUMENT_KEY = "budget.engagementDocument";

const useGetEngagementDocument = (engagementId?: string) =>
  useCustomQuery<IBudgetEngagementDocument | null>(
    [ENGAGEMENT_DOCUMENT_KEY, engagementId ?? ""],
    () => getEngagementDocumentApi(engagementId!),
    { enabled: !!engagementId, retry: false },
  );

const useAttachEngagementDocumentSignature = () => {
  const queryClient = useQueryClient();
  return useCustomMutation<IBudgetEngagementDocument, IAttachEngagementSignatureRequest>(
    attachEngagementDocumentSignatureApi,
    {
      onSuccess: (doc) => {
        queryClient.setQueryData([ENGAGEMENT_DOCUMENT_KEY, doc.EngagementId], doc);
        queryClient.invalidateQueries(["acceptance"]);
        queryClient.invalidateQueries({
          predicate: (q) => String(q.queryKey[0] ?? "").startsWith("acceptance"),
        });
      },
    },
  );
};

const useGetEngagementForApproval = (engagementId?: string) =>
  useCustomQuery<IBudgetEngagementListItem>(
    ["budget.engagementForApproval", engagementId ?? ""],
    () => getEngagementForApprovalApi(engagementId!),
    { enabled: !!engagementId, retry: false },
  );

const useCreateEngagement = () => {
  const queryClient = useQueryClient();
  return useCustomMutation<IBudgetEngagement, ICreateEngagementRequest>(
    createEngagementApi,
    {
      onSuccess: (engagement) => {
        queryClient.invalidateQueries(queryKeys.engagements(engagement.ExpenseId));
        queryClient.invalidateQueries(["budget.allEngagements"]);
        queryClient.invalidateQueries(["budget.expenses"]);
      },
    },
  );
};

const budgetService = {
  useGetBudgetYears,
  useGetDivisions,
  useGetChapters,
  useGetParagraphs,
  useGetGrantingProcedures,
  useGetExpenses,
  useGetExpense,
  useGetEngagements,
  useGetAllEngagements,
  useGetEngagementInvoices,
  useGetInvoices,
  useGetContracts,
  useGetTenders,
  useGetInvestmentCases,
  useGetExpensesReport,
  useCreateExpense,
  useUpdateExpense,
  useCreateEngagement,
  useDeleteEngagement,
  useSubmitEngagement,
  useGetEngagementForApproval,
  useGetEngagementDocument,
  useAttachEngagementDocumentSignature,
  getEngagementDocumentApi,
  attachEngagementDocumentSignatureApi,
  useGetSettleableEngagements,
  useGetEngagementSettlement,
  useGetInvoiceSettlements,
  useSaveSettlement,
  useDeleteSettlement,
};

export default budgetService;
